import {
  CAMPAIGN_LEVEL_LABELS,
  CAMPAIGN_LEVELS,
  CAMPAIGN_OBJECTIVES,
  defaultColumnsFor,
  EFFECTIVE_STATUSES,
  effectiveStatusInfo,
  objectiveLabel,
  roleHas,
  type BudgetChangeInput,
  type BulkResultDto,
  type CampaignEntityDto,
  type CampaignLevel,
  type DuplicateEntityInput,
  type TablePrefsDto,
  type UpdateCampaignEntityInput,
} from "@iagenix/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import {
  BarChart3,
  ChevronRight,
  Columns3,
  Download,
  LayoutTemplate,
  Megaphone,
  Plus,
  RefreshCw,
  Sparkles,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  AccountPicker,
  readLastAccount,
  rememberAccount,
} from "@/components/campaigns/account-picker";
import { AiPanel } from "@/components/ai/ai-panel";
import { BulkBar } from "@/components/campaigns/bulk-bar";
import { DetailPanel } from "@/components/campaigns/detail-panel";
import {
  BudgetDialog,
  BulkResultDialog,
  ColumnsDialog,
  DuplicateDialog,
} from "@/components/campaigns/dialogs";
import { EntityTable, type EntityTableMeta } from "@/components/campaigns/entity-table";
import { useCampaignMutations } from "@/components/campaigns/mutations";
import { PERIOD_ALL, PeriodPicker, type PeriodValue } from "@/components/campaigns/period-picker";
import { ClientFilter } from "@/components/client-filter";
import { RecreateDialog } from "@/components/creation/recreate-dialog";
import { creationApi, creationKey } from "@/lib/creation-api";
import { ConfirmDialog } from "@/components/confirm-dialog";
import { PageHeader } from "@/components/page-header";
import { EmptyState, ErrorState, LoadingState } from "@/components/states";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { api, errorMessage } from "@/lib/api";
import { queries } from "@/lib/queries";
import { reportsApi, reportsKey } from "@/lib/reports-api";
import { formatRelative } from "@/lib/utils";
import { periodToParams } from "./relatorios";

interface CampanhasSearch {
  conta?: string;
  aba?: CampaignLevel;
  campanha?: string;
  conjunto?: string;
  item?: string;
  nivel?: CampaignLevel;
}

const TODOS = "__todos__";
const ACT_RE = /^act_\d{1,30}$/;
const ID_RE = /^\d{1,30}$/;

function isLevel(v: unknown): v is CampaignLevel {
  return typeof v === "string" && (CAMPAIGN_LEVELS as readonly string[]).includes(v);
}

export const Route = createFileRoute("/app/$workspaceId/campanhas")({
  validateSearch: (raw: Record<string, unknown>): CampanhasSearch => {
    const out: CampanhasSearch = {};
    if (typeof raw.conta === "string" && ACT_RE.test(raw.conta)) out.conta = raw.conta;
    if (isLevel(raw.aba)) out.aba = raw.aba;
    if (typeof raw.campanha === "string" && ID_RE.test(raw.campanha)) out.campanha = raw.campanha;
    if (typeof raw.conjunto === "string" && ID_RE.test(raw.conjunto)) out.conjunto = raw.conjunto;
    if (typeof raw.item === "string" && ID_RE.test(raw.item)) out.item = raw.item;
    if (isLevel(raw.nivel)) out.nivel = raw.nivel;
    return out;
  },
  component: CampanhasPage,
});

/** Valor imediato (para o input) + valor atrasado (para a consulta), sem efeito. */
function useDebouncedInput(ms: number): [string, string, (value: string) => void] {
  const [value, setValue] = useState("");
  const [debounced, setDebounced] = useState("");
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const set = (next: string) => {
    setValue(next);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setDebounced(next), ms);
  };
  return [value, debounced, set];
}

function CampanhasPage() {
  const { workspaceId } = Route.useParams();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const queryClient = useQueryClient();
  const workspace = useQuery(queries.workspace(workspaceId));
  const [clientId, setClientId] = useState<string | undefined>(undefined);
  const accounts = useQuery(queries.campaignAccounts(workspaceId, clientId));
  const role = workspace.data?.role;
  const canWrite = role ? roleHas(role, "campaigns:write") : false;
  // Fase 8: "Perguntar à IA" e "Explicar" (Dono, Admin, Gestor e Analista).
  const canAi = role ? roleHas(role, "ai:use") : false;
  const [aiOpen, setAiOpen] = useState(false);

  type SearchPatch = { [K in keyof CampanhasSearch]?: CampanhasSearch[K] | undefined };
  const setSearch = (patch: SearchPatch, replace = false) =>
    void navigate({
      search: (prev) => {
        const merged: Record<string, unknown> = { ...prev, ...patch };
        const next: CampanhasSearch = {};
        for (const [k, v] of Object.entries(merged)) {
          if (v !== undefined) (next as Record<string, unknown>)[k] = v;
        }
        return next;
      },
      replace,
    });

  // Conta atual: da URL, senão a última aberta, senão a primeira da lista.
  const account = useMemo(() => {
    const list = accounts.data ?? [];
    if (search.conta && list.some((a) => a.metaId === search.conta)) return search.conta;
    const last = readLastAccount(workspaceId);
    if (last && list.some((a) => a.metaId === last)) return last;
    return list[0]?.metaId ?? "";
  }, [accounts.data, search.conta, workspaceId]);
  const level: CampaignLevel = search.aba ?? "campaigns";
  const current = accounts.data?.find((a) => a.metaId === account);
  const currency = current?.currency ?? "BRL";

  // Abrir a conta: marca a abertura e dispara a sincronização de estrutura se estiver velha.
  const mutations = useCampaignMutations(workspaceId);
  const opened = useRef<string | null>(null);
  const { open } = mutations;
  useEffect(() => {
    if (!account || opened.current === account) return;
    opened.current = account;
    rememberAccount(workspaceId, account);
    open.mutate(account);
  }, [account, workspaceId, open]);

  // Filtros
  const [q, dq, setQ] = useDebouncedInput(300);
  const [status, setStatus] = useState(TODOS);
  const [effective, setEffective] = useState(TODOS);
  const [objective, setObjective] = useState(TODOS);
  // Fase 6: o período filtra as MÉTRICAS (insights); a estrutura inteira continua listada.
  const [period, setPeriod] = useState<PeriodValue>({
    preset: "30d",
    range: { from: null, to: null },
  });

  const params = useMemo(() => {
    const p: Record<string, string> = {};
    if (search.campanha) p.campaignId = search.campanha;
    if (search.conjunto && level === "ads") p.adsetId = search.conjunto;
    if (dq.trim()) p.q = dq.trim();
    if (status !== TODOS) p.status = status;
    if (effective !== TODOS) p.effectiveStatus = effective;
    if (objective !== TODOS && level === "campaigns") p.objective = objective;
    if (period.preset && period.preset !== "maximo") p.preset = period.preset;
    else if (!period.preset) {
      if (period.range.from) p.from = period.range.from;
      if (period.range.to) p.to = period.range.to;
    }
    return p;
  }, [search.campanha, search.conjunto, level, dq, status, effective, objective, period]);

  const list = useQuery(queries.campaignList(workspaceId, account, level, params));
  const syncStatus = useQuery(queries.campaignSyncStatus(workspaceId, account));
  const insightsStatus = useQuery(queries.insightsStatus(workspaceId, account));
  const syncInsights = useMutation({
    mutationFn: () => reportsApi.syncInsights(workspaceId, account),
    onSuccess: async (r) => {
      await queryClient.invalidateQueries({ queryKey: reportsKey(workspaceId) });
      await queryClient.invalidateQueries({
        queryKey: ["workspaces", workspaceId, "campaigns", "list"],
      });
      toast.success(
        r.debounced
          ? "Sincronização de métricas já em andamento."
          : r.backfillDone
            ? "Métricas sincronizadas."
            : "Métricas sincronizando (primeira vez lê 90 dias em fatias).",
      );
    },
    onError: (err) => toast.error(errorMessage(err)),
  });
  const exportTable = useMutation({
    mutationFn: (format: "csv" | "xlsx") =>
      reportsApi.downloadExport(workspaceId, {
        kind: level,
        format,
        adAccountMetaId: account,
        ...(params.preset ? { preset: params.preset } : {}),
        ...(params.from ? { from: params.from } : {}),
        ...(params.to ? { to: params.to } : {}),
        ...(params.campaignId ? { campaignId: params.campaignId } : {}),
        ...(params.adsetId ? { adsetId: params.adsetId } : {}),
      }),
    onSuccess: (name) => toast.success(`Arquivo ${name} baixado.`),
    onError: (err) => toast.error(errorMessage(err)),
  });

  // Preferências de colunas (salvas no banco por usuário e aba).
  const prefsKey = `campaigns:${level}`;
  const prefs = useQuery(queries.tablePrefs(workspaceId, prefsKey));
  const columns =
    prefs.data && prefs.data.columns.length > 0 ? prefs.data.columns : defaultColumnsFor(level);
  const savePrefs = useMutation({
    mutationFn: (cols: string[]) =>
      api<TablePrefsDto>(`/api/workspaces/${workspaceId}/campaigns/table-prefs/${prefsKey}`, {
        method: "PUT",
        body: { columns: cols },
      }),
    onSuccess: (data) => {
      queryClient.setQueryData(queries.tablePrefs(workspaceId, prefsKey).queryKey, data);
      setColumnsOpen(false);
      toast.success("Colunas salvas.");
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  // Seleção: amarrada ao contexto (conta/aba/pai); mudar de contexto zera sem precisar de efeito.
  const selectionKey = `${account}|${level}|${search.campanha ?? ""}|${search.conjunto ?? ""}`;
  const [selection, setSelection] = useState<{ key: string; ids: Set<string> }>({
    key: selectionKey,
    ids: new Set(),
  });
  const selected = selection.key === selectionKey ? selection.ids : new Set<string>();
  const setSelected = (updater: Set<string> | ((prev: Set<string>) => Set<string>)) =>
    setSelection((prev) => {
      const base = prev.key === selectionKey ? prev.ids : new Set<string>();
      return { key: selectionKey, ids: typeof updater === "function" ? updater(base) : updater };
    });
  const items = useMemo(() => list.data?.items ?? [], [list.data]);
  const selectedItems = items.filter((i) => selected.has(i.metaId));

  // Diálogos
  const [budgetTargets, setBudgetTargets] = useState<CampaignEntityDto[]>([]);
  const [duplicateTargets, setDuplicateTargets] = useState<CampaignEntityDto[]>([]);
  const [deleteTargets, setDeleteTargets] = useState<CampaignEntityDto[]>([]);
  const [columnsOpen, setColumnsOpen] = useState(false);
  const [recreateTarget, setRecreateTarget] = useState<{
    entity: CampaignEntityDto;
    account: string;
  } | null>(null);
  const [bulkResult, setBulkResult] = useState<BulkResultDto | null>(null);
  const newCampaign = useMutation({
    mutationFn: () =>
      creationApi.createDraft(workspaceId, {
        adAccountMetaId: account,
        step: "campanha",
        data: {},
      }),
    onSuccess: (d) => {
      void queryClient.invalidateQueries({ queryKey: creationKey(workspaceId) });
      void navigate({
        to: "/app/$workspaceId/assistente/$draftId",
        params: { workspaceId, draftId: d.id },
      });
    },
    onError: (err) => toast.error(errorMessage(err)),
  });
  const saveTemplate = useMutation({
    mutationFn: (entity: CampaignEntityDto) =>
      creationApi.createTemplate(workspaceId, {
        name: entity.name,
        description: `Da campanha ${entity.metaId} (${account})`,
        campaignMetaId: entity.metaId,
      }),
    onSuccess: (t) => {
      void queryClient.invalidateQueries({ queryKey: creationKey(workspaceId) });
      toast.success(`Template "${t.name}" salvo (sem ids da conta).`);
    },
    onError: (err) => toast.error(errorMessage(err)),
  });
  const [pendingIds, setPendingIds] = useState<Set<string>>(new Set());

  const runBulk = async (
    action: "ativar" | "pausar" | "excluir" | "orcamento" | "duplicar",
    extra: { budget?: BudgetChangeInput; duplicate?: DuplicateEntityInput } = {},
  ) => {
    const ids = [...selected];
    if (ids.length === 0) return;
    const input = { level, adAccountMetaId: account, ids, action, ...extra };
    const result = await mutations.bulk.mutateAsync(input);
    setBulkResult(result);
    setSelected(new Set());
    setBudgetTargets([]);
    setDuplicateTargets([]);
    setDeleteTargets([]);
  };

  const editOne = async (entity: CampaignEntityDto, body: UpdateCampaignEntityInput) => {
    setPendingIds((p) => new Set(p).add(entity.metaId));
    try {
      await mutations.update.mutateAsync({ level: entity.level, metaId: entity.metaId, body });
      if (body.status)
        toast.success(`"${entity.name}" ${body.status === "ACTIVE" ? "ativado" : "pausado"}.`);
      else if (body.name) toast.success("Nome atualizado na Meta.");
      else toast.success("Orçamento atualizado na Meta.");
    } catch {
      /* o toast de erro sai da mutação */
    } finally {
      setPendingIds((p) => {
        const n = new Set(p);
        n.delete(entity.metaId);
        return n;
      });
    }
  };

  const drill = (entity: CampaignEntityDto) => {
    if (entity.level === "campaigns")
      setSearch({
        aba: "adsets",
        campanha: entity.metaId,
        conjunto: undefined,
        item: undefined,
        nivel: undefined,
      });
    else if (entity.level === "adsets")
      setSearch({
        aba: "ads",
        campanha: entity.campaignMetaId,
        conjunto: entity.metaId,
        item: undefined,
        nivel: undefined,
      });
  };

  const tableMeta: EntityTableMeta = {
    level,
    currency,
    canWrite,
    selected,
    pendingIds,
    onToggle: (id) =>
      setSelected((prev) => {
        const n = new Set(prev);
        if (n.has(id)) n.delete(id);
        else n.add(id);
        return n;
      }),
    onToggleAll: (ids) =>
      setSelected((prev) => {
        const all = ids.every((id) => prev.has(id));
        const n = new Set(prev);
        for (const id of ids)
          if (all) n.delete(id);
          else n.add(id);
        return n;
      }),
    onOpen: (entity) => setSearch({ item: entity.metaId, nivel: entity.level }),
    onDrill: drill,
    onDrillParent: (parentLevel, metaId) => {
      if (parentLevel === "campaigns")
        setSearch({
          aba: "campaigns",
          campanha: undefined,
          conjunto: undefined,
          item: metaId,
          nivel: "campaigns",
        });
      else setSearch({ aba: "adsets", conjunto: undefined, item: metaId, nivel: "adsets" });
    },
    onEdit: editOne,
    onBudget: (entity) => setBudgetTargets([entity]),
  };

  // Breadcrumb
  const campaignName = useMemo(() => {
    for (const i of items) {
      if (i.level === "campaigns" && i.metaId === search.campanha) return i.name;
      if (i.level !== "campaigns" && i.campaignMetaId === search.campanha) return i.campaignName;
    }
    return null;
  }, [items, search.campanha]);
  const adsetName = useMemo(() => {
    for (const i of items)
      if (i.level === "ads" && i.adsetMetaId === search.conjunto) return i.adsetName;
    return null;
  }, [items, search.conjunto]);

  const sync = list.data?.sync ?? syncStatus.data;
  const running = sync?.running ?? false;

  return (
    <>
      <PageHeader
        title="Campanhas"
        description="Tabela estilo Gerenciador de Anúncios lendo o espelho local; toda alteração vai primeiro para a Meta."
        actions={
          account ? (
            <>
              {canAi ? (
                <Button variant="outline" onClick={() => setAiOpen(true)}>
                  <Sparkles />
                  Perguntar à IA
                </Button>
              ) : null}
              {canWrite ? (
                <Button onClick={() => newCampaign.mutate()} loading={newCampaign.isPending}>
                  <Plus />
                  Nova campanha
                </Button>
              ) : null}
              {canWrite && level === "campaigns" && selectedItems.length === 1 ? (
                <Button
                  variant="outline"
                  loading={saveTemplate.isPending}
                  onClick={() => {
                    const only = selectedItems[0];
                    if (only) saveTemplate.mutate(only);
                  }}
                >
                  <LayoutTemplate />
                  Salvar como template
                </Button>
              ) : null}
              <Button
                variant="outline"
                onClick={() => setColumnsOpen(true)}
                disabled={prefs.isPending}
              >
                <Columns3 />
                Colunas
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    loading={exportTable.isPending}
                    aria-label="Exportar tabela"
                  >
                    <Download />
                    Exportar
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onSelect={() => exportTable.mutate("csv")}>
                    CSV
                  </DropdownMenuItem>
                  <DropdownMenuItem onSelect={() => exportTable.mutate("xlsx")}>
                    XLSX
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <Button
                variant="outline"
                onClick={() => syncInsights.mutate()}
                loading={syncInsights.isPending || (insightsStatus.data?.running ?? false)}
                title="Lê os insights da Meta (hoje a cada hora; 7 dias reprocessados; 90 dias na primeira vez)"
              >
                <BarChart3 />
                Sincronizar métricas
              </Button>
              <Button
                variant="outline"
                onClick={() => mutations.refresh.mutate(account)}
                loading={mutations.refresh.isPending || running}
              >
                <RefreshCw />
                Atualizar agora
              </Button>
            </>
          ) : null
        }
      />

      {accounts.isPending ? (
        <LoadingState rows={4} />
      ) : accounts.isError ? (
        <ErrorState error={accounts.error} onRetry={() => void accounts.refetch()} />
      ) : accounts.data.length === 0 ? (
        <EmptyState
          icon={<Megaphone className="size-8" aria-hidden />}
          title="Nenhuma conta de anúncio"
          description={
            clientId
              ? "Este cliente não tem contas vinculadas."
              : "Conecte uma conta Meta para ver as campanhas."
          }
          action={
            <Button asChild variant="outline">
              <Link to="/app/$workspaceId/integracoes" params={{ workspaceId }}>
                Ir para Integrações
              </Link>
            </Button>
          }
        />
      ) : (
        <div className="space-y-4">
          <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
            <ClientFilter workspaceId={workspaceId} value={clientId} onChange={setClientId} />
            <AccountPicker
              accounts={accounts.data}
              value={account}
              onChange={(metaId) =>
                setSearch({
                  conta: metaId,
                  campanha: undefined,
                  conjunto: undefined,
                  item: undefined,
                  nivel: undefined,
                })
              }
            />
            <p className="text-xs text-muted-foreground sm:ml-auto">
              {running
                ? "Sincronizando com a Meta…"
                : sync?.campaignsSyncedAt
                  ? `Última sincronização ${formatRelative(sync.campaignsSyncedAt)}`
                  : "Ainda não sincronizada"}
              {sync?.deliverySyncedAt && !running
                ? ` · entrega ${formatRelative(sync.deliverySyncedAt)}`
                : ""}
              {sync?.lastJob?.status === "erro" || sync?.lastJob?.status === "descartado" ? (
                <span className="text-destructive"> · falhou: {sync.lastJob.lastError}</span>
              ) : null}
              {list.data?.insights ? (
                <>
                  {" · métricas "}
                  {list.data.insights.running
                    ? "sincronizando…"
                    : list.data.insights.insightsSyncedAt
                      ? `${formatRelative(list.data.insights.insightsSyncedAt)} (${list.data.insights.daysWithData} dias${list.data.insights.backfillDone ? "" : ", lendo histórico"})`
                      : "ainda não sincronizadas"}
                  {list.data.insights.lastError ? (
                    <span className="text-destructive"> · {list.data.insights.lastError}</span>
                  ) : null}
                </>
              ) : null}
            </p>
          </div>

          <nav aria-label="Contexto" className="flex flex-wrap items-center gap-1 text-sm">
            <button
              type="button"
              className={!search.campanha ? "font-medium" : "text-muted-foreground hover:underline"}
              onClick={() =>
                setSearch({ aba: "campaigns", campanha: undefined, conjunto: undefined })
              }
            >
              Todas as campanhas
            </button>
            {search.campanha ? (
              <>
                <ChevronRight className="size-3 text-muted-foreground" aria-hidden />
                <button
                  type="button"
                  className={
                    !search.conjunto ? "font-medium" : "text-muted-foreground hover:underline"
                  }
                  onClick={() => setSearch({ aba: "adsets", conjunto: undefined })}
                >
                  {campaignName ?? `Campanha ${search.campanha}`}
                </button>
              </>
            ) : null}
            {search.conjunto && level === "ads" ? (
              <>
                <ChevronRight className="size-3 text-muted-foreground" aria-hidden />
                <span className="font-medium">{adsetName ?? `Conjunto ${search.conjunto}`}</span>
              </>
            ) : null}
          </nav>

          <Card>
            <CardContent className="space-y-4">
              <Tabs
                value={level}
                onValueChange={(v) => {
                  if (!isLevel(v)) return;
                  setSearch({
                    aba: v,
                    conjunto: v === "ads" ? search.conjunto : undefined,
                    campanha: v === "campaigns" ? undefined : search.campanha,
                  });
                }}
              >
                <TabsList>
                  {CAMPAIGN_LEVELS.map((l) => (
                    <TabsTrigger key={l} value={l}>
                      {CAMPAIGN_LEVEL_LABELS[l].plural}
                    </TabsTrigger>
                  ))}
                </TabsList>
              </Tabs>

              <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
                <Input
                  placeholder="Buscar por nome…"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  className="sm:max-w-xs"
                  aria-label="Buscar por nome"
                />
                <Select value={status} onValueChange={setStatus}>
                  <SelectTrigger className="sm:w-40" aria-label="Status configurado">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={TODOS}>Qualquer status</SelectItem>
                    <SelectItem value="ACTIVE">Ativos</SelectItem>
                    <SelectItem value="PAUSED">Pausados</SelectItem>
                    <SelectItem value="ARCHIVED">Arquivados</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={effective} onValueChange={setEffective}>
                  <SelectTrigger className="sm:w-48" aria-label="Status de entrega">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={TODOS}>Qualquer veiculação</SelectItem>
                    {EFFECTIVE_STATUSES.filter((s) => s !== "DELETED").map((s) => (
                      <SelectItem key={s} value={s}>
                        {effectiveStatusInfo(s).label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {level === "campaigns" ? (
                  <Select value={objective} onValueChange={setObjective}>
                    <SelectTrigger className="sm:w-44" aria-label="Objetivo">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={TODOS}>Todos os objetivos</SelectItem>
                      {CAMPAIGN_OBJECTIVES.map((o) => (
                        <SelectItem key={o} value={o}>
                          {objectiveLabel(o)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : null}
                <PeriodPicker
                  value={period}
                  onChange={(v) => setPeriod(v.preset === "maximo" ? PERIOD_ALL : v)}
                  className="sm:ml-auto"
                />
              </div>

              {list.isPending ? (
                <LoadingState rows={6} />
              ) : list.isError ? (
                <ErrorState error={list.error} onRetry={() => void list.refetch()} />
              ) : list.data.items.length === 0 && Object.keys(params).length === 0 ? (
                <EmptyState
                  icon={<Megaphone className="size-8" aria-hidden />}
                  title={running ? "Sincronizando com a Meta…" : "Nada por aqui"}
                  description={
                    running
                      ? "A estrutura da conta está sendo lida. Isso leva alguns segundos."
                      : `Esta conta não tem ${CAMPAIGN_LEVEL_LABELS[level].plural.toLowerCase()}${search.campanha ? " neste contexto" : ""}. Use "Nova campanha" para criar pelo assistente.`
                  }
                />
              ) : (
                <EntityTable
                  data={list.data.items}
                  totals={list.data.totals}
                  columns={columns}
                  meta={tableMeta}
                />
              )}
            </CardContent>
          </Card>

          <BulkBar
            count={selected.size}
            canWrite={canWrite}
            busy={mutations.bulk.isPending}
            level={level}
            onClear={() => setSelected(new Set())}
            onActivate={() => void runBulk("ativar")}
            onPause={() => void runBulk("pausar")}
            onBudget={() => setBudgetTargets(selectedItems)}
            onDuplicate={() => setDuplicateTargets(selectedItems)}
            onDelete={() => setDeleteTargets(selectedItems)}
          />
        </div>
      )}

      {budgetTargets.length > 0 ? (
        <BudgetDialog
          open
          onOpenChange={(o) => (o ? undefined : setBudgetTargets([]))}
          currency={currency}
          targets={budgetTargets}
          loading={mutations.update.isPending || mutations.bulk.isPending}
          onSubmit={(change) => {
            const single = budgetTargets.length === 1 ? budgetTargets[0] : undefined;
            if (single && !selected.has(single.metaId)) {
              void editOne(single, { budget: change }).then(() => setBudgetTargets([]));
            } else {
              void runBulk("orcamento", { budget: change });
            }
          }}
        />
      ) : null}

      {duplicateTargets.length > 0 ? (
        <DuplicateDialog
          open
          onOpenChange={(o) => (o ? undefined : setDuplicateTargets([]))}
          level={level}
          count={duplicateTargets.length}
          {...(duplicateTargets.length === 1 ? { name: duplicateTargets[0]?.name ?? "" } : {})}
          accounts={accounts.data ?? []}
          currentAccount={account}
          loading={mutations.duplicate.isPending || mutations.bulk.isPending}
          onRecreate={(target) => {
            const single = duplicateTargets[0];
            if (!single) return;
            setDuplicateTargets([]);
            setRecreateTarget({ entity: single, account: target });
          }}
          onSubmit={(input) => {
            const single = duplicateTargets.length === 1 ? duplicateTargets[0] : undefined;
            if (single && !selected.has(single.metaId)) {
              void mutations.duplicate
                .mutateAsync({ level: single.level, metaId: single.metaId, body: input })
                .then(() => setDuplicateTargets([]))
                .catch(() => undefined);
            } else {
              void runBulk("duplicar", { duplicate: input });
            }
          }}
        />
      ) : null}

      <ConfirmDialog
        open={deleteTargets.length > 0}
        onOpenChange={(o) => (o ? undefined : setDeleteTargets([]))}
        title={
          deleteTargets.length === 1
            ? `Excluir "${deleteTargets[0]?.name ?? ""}"?`
            : `Excluir ${deleteTargets.length} ${CAMPAIGN_LEVEL_LABELS[level].plural.toLowerCase()}?`
        }
        description="A Meta marca o item como excluído (status DELETED). Ele some das listagens, mas o histórico de métricas continua na Meta. Não dá para desfazer."
        confirmLabel="Excluir na Meta"
        destructive
        loading={mutations.remove.isPending || mutations.bulk.isPending}
        onConfirm={() => {
          const single = deleteTargets.length === 1 ? deleteTargets[0] : undefined;
          if (single && !selected.has(single.metaId)) {
            void mutations.remove
              .mutateAsync({ level: single.level, metaId: single.metaId })
              .then(() => setDeleteTargets([]))
              .catch(() => undefined);
          } else {
            void runBulk("excluir");
          }
        }}
      />

      {columnsOpen ? (
        <ColumnsDialog
          open
          onOpenChange={setColumnsOpen}
          level={level}
          value={columns}
          saving={savePrefs.isPending}
          onSave={(cols) => savePrefs.mutate(cols)}
        />
      ) : null}

      <BulkResultDialog result={bulkResult} onClose={() => setBulkResult(null)} />

      {recreateTarget ? (
        <RecreateDialog
          workspaceId={workspaceId}
          level={recreateTarget.entity.level}
          metaId={recreateTarget.entity.metaId}
          name={recreateTarget.entity.name}
          sourceAccount={account}
          accounts={accounts.data ?? []}
          initialTarget={recreateTarget.account}
          onClose={() => setRecreateTarget(null)}
        />
      ) : null}

      {aiOpen && account ? (
        <AiPanel
          workspaceId={workspaceId}
          context={{ ...periodToParams(period), accounts: [account] }}
          subtitle="Contexto: a conta e o período da tabela (KPIs, campanhas, conjuntos, anúncios, criativos, quebras, metas e alertas)."
          onClose={() => setAiOpen(false)}
        />
      ) : null}

      {search.item && search.nivel ? (
        <DetailPanel
          workspaceId={workspaceId}
          level={search.nivel}
          metaId={search.item}
          period={periodToParams(period)}
          canExplain={canAi}
          onClose={() => setSearch({ item: undefined, nivel: undefined })}
          onDrill={(entity) => {
            drill(entity);
          }}
        />
      ) : null}
    </>
  );
}
